<template>
  <b-modal
    :id="id"
    ref="modal"
    size="xl"
    hide-header
    hide-footer
    body-class="p-0 overflow-hidden"
    content-class="border-0 shadow-lg"
    dialog-class="modal-xl-custom"
    @shown="onShown"
    @hidden="onHidden"
  >
    <!-- ─── HEADER ─── -->
    <div class="ev-modal-header">
      <div class="ev-modal-header__info">
        <div class="ev-modal-header__icon">
          <i class="fa fa-truck"></i>
        </div>
        <div>
          <h5 class="ev-modal-header__title">Carregamento de Pedido #{{ pedidoId }}</h5>
          <div class="ev-modal-header__sub">
            <b-badge :variant="situacaoVariant(venda.SITUACAO)" pill class="mr-2">{{ situacaoTexto(venda.SITUACAO) }}</b-badge>
            <span>{{ venda.NOME_CLIENTE || '...' }}</span>
          </div>
        </div>
      </div>
      <b-button variant="link" class="ev-modal-header__close" @click="fecharModal">
        <i class="fa fa-times"></i>
      </b-button>
    </div>

    <!-- ─── CORPO REFEITO (FEED DE CARDS) ─── -->
    <div class="ev-modal-body p-0 bg-soft-grey d-flex flex-column" style="height: calc(100vh - 160px); overflow: hidden;">
      <b-overlay :show="carregando" rounded="sm" opacity="0.6" class="w-100 h-100 d-flex flex-column">
        
        <!-- HEADER FIXO DE RESUMO (Acima do Scroll) -->
        <div class="ev-fixed-top-bar border-bottom bg-white d-flex align-items-center px-3 py-2 shadow-sm">
          <div class="ev-sidebar-title" style="width: 255px;">
            <span class="font-weight-bold text-dark small"><i class="fa fa-tasks mr-2 text-primary"></i>CHECKLIST ITENS</span>
          </div>
          <div class="flex-grow-1 d-flex align-items-center gap-3">
             <div class="ev-stat-mini">
                <label>TOTAL PEDIDO</label>
                <div class="value text-primary">{{ formatarQuant(totalPedido) }}</div>
             </div>
             <div class="ev-stat-mini">
                <label>EMBARCADO</label>
                <div class="value text-success">{{ formatarQuant(totalEmbarcado) }}</div>
             </div>
             <div class="ev-stat-mini flex-grow-1 max-w-400">
                <div class="d-flex justify-content-between align-items-end mb-1">
                  <label class="m-0">PROGRESSO TOTAL</label>
                  <span class="font-weight-bold text-dark small">{{ progresso }}%</span>
                </div>
                <b-progress :value="progresso" :max="100" :variant="progressoVariant" style="height: 10px; border-radius: 5px;" />
             </div>
          </div>
        </div>

        <div class="d-flex flex-grow-1 overflow-hidden">
          <!-- Sidebar de Navegação Rápida -->
          <div class="ev-sidebar d-none d-lg-flex flex-column border-right bg-white" style="width: 280px;">
            <div class="flex-grow-1 overflow-auto p-2">
              <div v-for="(item, idx) in itens" :key="'nav-'+idx" 
                   class="ev-nav-item p-3 mb-2 rounded border d-flex align-items-center gap-3"
                   :class="{'ev-nav-item--active': activeIndex === idx, 'ev-nav-item--done': calcProgressoItem(item) >= 100}"
                   @click="scrollToItem(idx)">
                <div class="ev-nav-dot"></div>
                <div class="flex-grow-1 overflow-hidden">
                  <div class="text-truncate font-weight-bold" :class="activeIndex === idx ? 'text-primary' : 'text-dark'" style="font-size: 0.85rem">{{ item.NOME_PRODUTO }}</div>
                  <div class="font-xs text-muted">{{ formatarQuant(item.QUANT_EDIT) }} / {{ formatarQuant(item.QUANT_PEDIDO) }}</div>
                </div>
                <i v-if="calcProgressoItem(item) >= 100" class="fa fa-check-circle text-success"></i>
              </div>
            </div>
          </div>

          <!-- Feed Principal de Cards -->
          <div class="ev-feed flex-grow-1 overflow-auto p-4" id="ev-scroll-container">
            <!-- Lista de Cards de Itens -->
            <div v-for="(item, idx) in itens" :key="item.CONTROLE" :id="'item-card-'+idx" 
                 class="ev-item-card mb-4 bg-white shadow-sm border rounded-lg overflow-hidden"
                 :class="{'ev-item-card--done': calcProgressoItem(item) >= 100, 'ev-item-card--active': activeIndex === idx}">
              
              <!-- Header do Card -->
              <div class="ev-item-card-header p-3 border-bottom d-flex align-items-center justify-content-between"
                   :class="calcProgressoItem(item) >= 100 ? 'bg-soft-success' : (activeIndex === idx ? 'bg-soft-blue' : 'bg-light')">
                <div class="d-flex align-items-center gap-3">
                  <div class="ev-item-index">{{ idx + 1 }}</div>
                  <div>
                    <h5 class="m-0 font-weight-bold text-dark">{{ item.NOME_PRODUTO }}</h5>
                    <div class="font-xs text-muted text-uppercase">CÓDIGO: {{ item.COD_PRO }} | UNIDADE: {{ item.UNIDADE }}</div>
                  </div>
                </div>
                <div class="text-right">
                  <div class="ev-quant-destaque text-primary m-0">{{ formatarQuant(item.QUANT_PEDIDO) }} <small>{{ item.UNIDADE }}</small></div>
                  <div class="font-xs text-muted text-uppercase">Pedido Original</div>
                </div>
              </div>

              <!-- Corpo do Card -->
              <div class="p-4">
                <!-- Caso o item use LOTES -->
                <div v-if="parametrosGerais.OP_EXPEDICAO_POR_LOTE === '1' && item.lotes && item.lotes.length > 0">
                  <div class="d-flex align-items-center justify-content-between mb-3">
                    <div class="font-weight-bold text-muted small"><i class="fa fa-cubes mr-2"></i>APONTAMENTO POR LOTE</div>
                    <b-button v-if="podeEditar" size="sm" variant="outline-primary" pill @click="usarTudoPlanejado(item)">
                      USAR TUDO PLANEJADO
                    </b-button>
                  </div>

                  <div class="ev-lotes-grid">
                    <div v-for="lote in item.lotes" :key="lote.LOTE" 
                         class="ev-lote-card p-3 mb-2 rounded border d-flex align-items-center gap-4 transition-all"
                         :class="{'ev-lote-card--active': Number(lote.QUANT_CARREGADA) > 0}">
                      <div class="flex-grow-1">
                        <div class="font-weight-bold text-dark">LOTE: {{ lote.LOTE }}</div>
                        <div class="font-xs text-muted">Planejado: <strong>{{ formatarQuant(lote.QUANT_PLANEJADA) }}</strong> | Saldo: {{ formatarQuant(lote.QUANT_SALDO) }}</div>
                      </div>

                      <div class="d-flex align-items-center gap-2">
                        <b-button v-if="podeEditar" variant="soft-info" size="sm" pill class="px-3" @click="lote.QUANT_CARREGADA = lote.QUANT_PLANEJADA; onLoteQuantChange(item)">
                          USAR PLAN.
                        </b-button>
                        <div style="width: 160px">
                          <vue-autonumeric
                            v-model="lote.QUANT_CARREGADA"
                            :options="{ digitGroupSeparator: '.', decimalCharacter: ',', decimalPlaces: 3, minimumValue: '0', outputFormat: 'number' }"
                            class="form-control form-control-lg text-right font-weight-bold ev-input-large"
                            @input="onLoteQuantChange(item)"
                            :disabled="!podeEditar"
                          />
                        </div>
                        <b-button v-if="podeEditar" variant="soft-danger" size="sm" pill @click="lote.QUANT_CARREGADA = 0; onLoteQuantChange(item)">
                          <i class="fa fa-times"></i>
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Caso o item NÃO use lotes (Edição Direta) -->
                <div v-else class="d-flex align-items-center justify-content-between p-3 bg-light rounded shadow-inner"
                     :class="{'bg-soft-blue border-primary': Number(item.QUANT_EDIT) > 0}">
                  <div>
                    <div class="font-weight-bold text-dark">QUANTIDADE PARA EMBARQUE</div>
                    <div class="font-xs text-muted text-uppercase">Sem controle de lote para este produto</div>
                  </div>
                  <div style="width: 220px">
                    <vue-autonumeric
                      v-model="item.QUANT_EDIT"
                      :options="{ digitGroupSeparator: '.', decimalCharacter: ',', decimalPlaces: 3, minimumValue: '0', maximumValue: String(item.QUANT_PEDIDO), outputFormat: 'number' }"
                      class="form-control form-control-lg text-right font-weight-bold ev-input-large"
                      :class="{'border-primary': Number(item.QUANT_EDIT) > 0}"
                      @input="onQuantChange(item)"
                      :disabled="!podeEditar"
                    />
                  </div>
                </div>

                <!-- Barra de Progresso do Card -->
                <div class="mt-4 pt-3 border-top d-flex align-items-center justify-content-between">
                  <div class="flex-grow-1 mr-4">
                    <b-progress :value="calcProgressoItem(item)" :max="100" :variant="progressoItemVariant(item)" style="height: 8px; border-radius: 4px;" />
                  </div>
                  <div class="text-right" style="min-width: 140px;">
                    <div class="h5 m-0 font-weight-bold" :class="progressoItemClass(item)">{{ formatarQuant(item.QUANT_EDIT) }} <small>{{ item.UNIDADE }}</small></div>
                    <div class="font-xs text-muted text-uppercase">Total Embarcado ({{ calcProgressoItem(item) }}%)</div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Espaçador final para o scroll -->
            <div style="height: 100px;"></div>
          </div>
        </div>
      </b-overlay>
    </div>
      </b-overlay>
    </div>

    <!-- ─── RODAPÉ ─── -->
    <div class="ev-modal-footer p-3 bg-light border-top">
      <b-row class="w-100 m-0">
        <b-col class="p-0">
          <b-button variant="soft-secondary" pill class="px-4 font-weight-bold" @click="fecharModal">
            FECHAR
          </b-button>
        </b-col>
        <b-col class="p-0 text-right d-flex justify-content-end gap-2">
          <b-button
            v-if="podeEditar"
            variant="soft-info"
            pill
            class="px-4 font-weight-bold mr-2"
            @click="salvarQuantidades"
            :disabled="salvando || fechando || carregando"
          >
            <i class="fa fa-save mr-1" :class="{'fa-spin fa-refresh': salvando}"></i> SALVAR
          </b-button>
          <b-button
            v-if="podeEditar"
            variant="success"
            pill
            class="px-4 font-weight-bold shadow-sm"
            @click="confirmarFechamento"
            :disabled="salvando || fechando || carregando"
          >
            <i class="fa fa-check-circle mr-1" :class="{'fa-spin fa-refresh': fechando}"></i> FINALIZAR EMBARQUE
          </b-button>
        </b-col>
      </b-row>
    </div>

    <!-- Mini Modal de Confirmação -->
    <b-modal id="modal-confirm-fechar" title="Confirmar Finalização" centered @ok="fecharPedido" ok-title="Confirmar" cancel-title="Voltar" ok-variant="success">
      <p>Deseja finalizar o embarque do pedido <strong>#{{ pedidoId }}</strong>?</p>
      <div class="bg-light p-3 rounded">
        <div class="d-flex justify-content-between mb-1">
          <span>Total Embarcado:</span>
          <strong class="text-success">{{ formatarQuant(totalEmbarcado) }} kg</strong>
        </div>
        <div class="d-flex justify-content-between">
          <span>Progresso:</span>
          <strong :class="progressoVariant === 'success' ? 'text-success' : 'text-warning'">{{ progresso }}%</strong>
        </div>
      </div>
      <b-alert show variant="warning" class="mt-3 py-2" v-if="progresso < 100">
        <i class="fa fa-warning mr-1"></i> O pedido será fechado com pendência de <strong>{{ 100 - progresso }}%</strong>.
      </b-alert>
    </b-modal>
  </b-modal>
</template>

<script>
import VueAutonumeric from 'vue-autonumeric'
import { imprimirDocumentoCarregamento } from './ExpedicaoVolumesPrint'

export default {
  name: 'ExpedicaoVolumesModal',
  components: {
    VueAutonumeric
  },
  props: {
    id: { type: String, default: 'modal-carregamento' },
    pedidoId: { type: [String, Number], default: null }
  },
  data () {
    return {
      carregando: false,
      salvando: false,
      fechando: false,
      venda: {},
      itens: [],
      campos: [
        { key: 'ITEM', label: '#', thClass: 'ev-modal-th text-center', tdClass: 'text-center text-muted font-xs' },
        { key: 'COD_PRO', label: 'Cód', thClass: 'ev-modal-th', tdClass: 'font-weight-bold font-xs' },
        { key: 'NOME_PRODUTO', label: 'Produto', thClass: 'ev-modal-th', tdClass: 'font-sm' },
        { key: 'UNIDADE', label: 'Un', thClass: 'ev-modal-th text-center', tdClass: 'text-center font-xs' },
        { key: 'QUANT_PEDIDO', label: 'Pedida', thClass: 'ev-modal-th text-right', tdClass: 'text-right' },
        { key: 'QUANT', label: 'Embarcada', thClass: 'ev-modal-th text-right', tdClass: 'text-right' },
        { key: 'PROGRESSO', label: 'Status', thClass: 'ev-modal-th', tdClass: 'vertical-middle' }
      ],
      parametrosGerais: {
        OP_EXPEDICAO_POR_LOTE: '0'
      },
      activeIndex: 0
    }
  },
  computed: {
    podeEditar () {
      return ['4', '5'].includes(String(this.venda.SITUACAO || '').trim())
    },
    totalPedido () {
      return this.itens.reduce((s, i) => s + Number(i.QUANT_PEDIDO || 0), 0)
    },
    totalEmbarcado () {
      return this.itens.reduce((s, i) => s + Number(i.QUANT_EDIT ?? i.QUANT ?? 0), 0)
    },
    progresso () {
      if (this.totalPedido === 0) return 0
      return Math.min(100, Math.round((this.totalEmbarcado / this.totalPedido) * 100))
    },
    progressoVariant () {
      if (this.progresso >= 100) return 'success'
      if (this.progresso >= 50) return 'warning'
      return 'info'
    }
  },
  methods: {
    async onShown () {
      if (this.pedidoId) {
        await this.carregarParametros()
        this.carregarDados()
      }
    },
    onHidden () {
      this.venda = {}
      this.itens = []
      this.$emit('closed')
    },
    fecharModal () {
      this.$bvModal.hide(this.id)
    },
    async carregarDados () {
      this.carregando = true
      try {
        const result = await this.$http.get(`/api/protected/terminal/expedicao/volumes/getItensPedido/${this.pedidoId}`)
        this.venda = result.data.venda || {}
        
        let temLotes = false
        this.itens = (result.data.itens || []).map(i => {
          const item = {
            ...i,
            QUANT_EDIT: 0,
            _showDetails: false,
            lotes: (i.lotes || []).map(l => ({
              ...l,
              QUANT_CARREGADA: Number(l.QUANT_CARREGADA || 0)
            }))
          }

          if (item.lotes && item.lotes.length > 0) temLotes = true

          // Se houver lotes, a quantidade embarcada é a soma deles
          if (item.lotes && item.lotes.length > 0) {
            item.QUANT_EDIT = item.lotes.reduce((acc, l) => acc + Number(l.QUANT_CARREGADA || 0), 0)
          } else {
            item.QUANT_EDIT = i.QUANT != null ? Number(i.QUANT) : 0
          }
          
          return item
        })

        // Forçar ativação do modo lote se houver dados de lote
        if (temLotes) {
          this.parametrosGerais.OP_EXPEDICAO_POR_LOTE = '1'
        }

        if (String(this.venda.SITUACAO || '').trim() === '4') {
          await this.iniciarCarregamento()
        }

        // Foco no primeiro input após carregar dados e renderizar
        this.$nextTick(() => {
          setTimeout(() => {
            const firstInput = document.querySelector(`#${this.id} .ev-input-quant input`) || 
                               document.querySelector(`#${this.id} .ev-input-quant`)
            if (firstInput) {
              firstInput.focus()
              if (typeof firstInput.select === 'function') firstInput.select()
            }
          }, 1000)
        })
      } catch (err) {
        this.$bvToast.toast('Erro ao carregar itens: ' + (err.response?.data || err.message), {
          title: 'Erro', variant: 'danger', autoHideDelay: 5000
        })
        this.fecharModal()
      } finally {
        this.carregando = false
      }
    },
    carregarParametros() {
      return this.$http.get('/api/protected/parametro/getDados')
        .then(res => {
          if (res.data && res.data.length > 0) {
            const p = res.data[0]
            this.parametrosGerais.OP_EXPEDICAO_POR_LOTE = String(p.OP_EXPEDICAO_POR_LOTE || '0').trim()
          }
        })
        .catch(err => {
          console.error('Erro ao carregar parâmetros:', err)
        })
    },
    toggleLotes(item) {
      if (this.parametrosGerais.OP_EXPEDICAO_POR_LOTE !== '1') return
      this.$set(item, '_showDetails', !item._showDetails)
    },
    onLoteQuantChange(item) {
      item.QUANT_EDIT = item.lotes.reduce((acc, l) => acc + Number(l.QUANT_CARREGADA || 0), 0)
    },
    usarTudoPlanejado(item) {
      if (!item.lotes) return
      item.lotes.forEach(l => {
        l.QUANT_CARREGADA = Number(l.QUANT_PLANEJADA || 0)
      })
      this.onLoteQuantChange(item)
    },
    scrollToItem(idx) {
      this.activeIndex = idx
      const cardId = 'item-card-' + idx
      const el = document.getElementById(cardId)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        
        // Foco automático no primeiro input do card
        this.$nextTick(() => {
          setTimeout(() => {
            const firstInput = document.querySelector(`#${cardId} input`)
            if (firstInput) {
              firstInput.focus()
              if (typeof firstInput.select === 'function') firstInput.select()
            }
          }, 400) // Aguarda o fim do scroll suave
        })
      }
    },
    async iniciarCarregamento () {
      try {
        await this.$http.post('/api/protected/terminal/expedicao/volumes/iniciarCarregamento', {
          pedidoId: this.pedidoId,
          usuario: window.localStorage.getItem('login')
        })
        this.venda.SITUACAO = '5'
      } catch (err) {}
    },
    async salvarQuantidades () {
      if (!this.podeEditar) return
      if (!this.validarTodos()) return

      this.salvando = true
      try {
        const itensPayload = this.itens.map(i => ({
          CONTROLE: i.CONTROLE,
          QUANT: Number(i.QUANT_EDIT ?? 0),
          QUANT_PEDIDO: Number(i.QUANT_PEDIDO || 0),
          lotes: i.lotes
        }))

        await this.$http.post('/api/protected/terminal/expedicao/volumes/salvarQuantidades', {
          pedidoId: this.pedidoId,
          itens: itensPayload
        })

        this.$bvToast.toast('Progresso salvo com sucesso!', { title: 'Sucesso', variant: 'success', autoHideDelay: 2000 })
        this.$emit('updated')
      } catch (err) {
        this.$bvToast.toast('Erro ao salvar: ' + (err.response?.data || err.message), { title: 'Erro', variant: 'danger' })
      } finally {
        this.salvando = false
      }
    },
    confirmarFechamento () {
      if (!this.validarTodos()) return
      this.$bvModal.show('modal-confirm-fechar')
    },
    async fecharPedido () {
      this.fechando = true
      try {
        const itensPayload = this.itens.map(i => ({
          CONTROLE: i.CONTROLE,
          QUANT: Number(i.QUANT_EDIT ?? 0),
          QUANT_PEDIDO: Number(i.QUANT_PEDIDO || 0),
          lotes: i.lotes
        }))
        await this.$http.post('/api/protected/terminal/expedicao/volumes/salvarQuantidades', {
          pedidoId: this.pedidoId,
          itens: itensPayload
        })

        await this.$http.post('/api/protected/terminal/expedicao/volumes/fecharPedido', {
          pedidoId: this.pedidoId,
          usuario: window.localStorage.getItem('login') || 'SISTEMA'
        })

        this.$bvToast.toast(`Pedido #${this.pedidoId} finalizado com sucesso!`, { title: 'Concluído', variant: 'success' })
        
        // Perguntar se deseja imprimir
        try {
          const confirm = await this.$bvModal.msgBoxConfirm('Deseja imprimir o comprovante de embarque?', {
            title: 'Pedido Finalizado',
            okTitle: 'Sim, Imprimir',
            cancelTitle: 'Não',
            centered: true,
            buttonSize: 'sm',
            okVariant: 'success',
            headerClass: 'p-2 border-bottom-0',
            footerClass: 'p-2 border-top-0',
          })

          if (confirm) {
            await imprimirDocumentoCarregamento(this.pedidoId, this.$http)
          }
        } catch (e) {
          // Usuário fechou o box de confirmação sem clicar em nada
        }

        this.$emit('updated')
        this.fecharModal()
      } catch (err) {
        this.$bvToast.toast('Erro ao finalizar: ' + (err.response?.data || err.message), { title: 'Erro', variant: 'danger' })
      } finally {
        this.fechando = false
      }
    },
    onQuantChange (item) {
      if (Number(item.QUANT_EDIT) > Number(item.QUANT_PEDIDO)) {
        item.QUANT_EDIT = Number(item.QUANT_PEDIDO)
      }
    },
    validarTodos () {
      for (const item of this.itens) {
        const q = Number(item.QUANT_EDIT || 0)
        const p = Number(item.QUANT_PEDIDO || 0)
        if (q < 0 || q > p) {
          this.$bvToast.toast('Existem quantidades inválidas', { variant: 'warning' })
          return false
        }
      }
      return true
    },
    calcProgressoItem (item) {
      const p = Number(item.QUANT_PEDIDO || 0)
      if (p === 0) return 0
      const e = Number(item.QUANT_EDIT ?? item.QUANT ?? 0)
      return Math.min(100, Math.round((e / p) * 100))
    },
    progressoItemVariant (item) {
      const p = this.calcProgressoItem(item)
      if (p >= 100) return 'success'
      if (p > 0) return 'warning'
      return 'secondary'
    },
    progressoItemClass (item) {
      const p = this.calcProgressoItem(item)
      if (p >= 100) return 'text-success'
      if (p > 0) return 'text-warning'
      return 'text-muted'
    },
    situacaoTexto (s) {
      return { '4': 'Em Ordem', '5': 'Embarcando', '6': 'Embarcado' }[String(s || '').trim()] || s
    },
    situacaoVariant (s) {
      return { '4': 'info', '5': 'warning', '6': 'success' }[String(s || '').trim()] || 'secondary'
    },
    formatarQuant (v) {
      return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3 })
    },
    formatarData (v) {
      if (!v) return '-'
      return new Date(v).toLocaleDateString('pt-BR')
    }
  }
}
</script>

<style scoped>
/* ── HEADER PREMIUM ── */
.ev-modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 24px;
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: #fff;
}
.ev-modal-header__info { display: flex; align-items: center; gap: 16px; }
.ev-modal-header__icon {
  width: 48px; height: 48px;
  background: rgba(255,255,255,0.1);
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px; color: #fbbf24;
  border: 1px solid rgba(255,255,255,0.1);
}
.ev-modal-header__title { margin: 0; font-weight: 700; font-size: 1.15rem; letter-spacing: 0.5px; }
.ev-modal-header__sub { font-size: 0.85rem; color: rgba(255,255,255,0.6); display: flex; align-items: center; margin-top: 2px; }
.ev-modal-header__close { color: rgba(255,255,255,0.5); padding: 0; font-size: 1.2rem; }
.ev-modal-header__close:hover { color: #fff; transform: rotate(90deg); transition: all 0.2s; }

/* ── SUMMARY ── */
.ev-summary-item {
  background: #f8fafc;
  border-radius: 12px;
  padding: 12px 16px;
  border-left: 4px solid #cbd5e1;
  height: 100%;
}
.ev-summary-value { font-size: 1.2rem; font-weight: 700; color: #1e293b; margin-top: 2px; }
.ev-summary-value small { font-size: 0.7rem; color: #64748b; text-transform: uppercase; }

/* ── TABLE ── */
.ev-modal-thead th, .ev-modal-th {
  background: #f1f5f9 !important;
  color: #475569 !important;
  font-size: 0.7rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  padding: 10px 12px !important;
  border: none !important;
}
.ev-modal-table td { padding: 8px 12px !important; vertical-align: middle !important; border-top: 1px solid #f1f5f9 !important; }

.ev-input-container { width: 120px; margin-left: auto; }
.bg-soft-success { background-color: rgba(16, 185, 129, 0.1) !important; }
.bg-soft-info { background-color: rgba(59, 130, 246, 0.1) !important; }

/* ── FOOTER ── */
.modal-footer-premium { border-top: 1px solid #e2e8f0; }
.btn-soft-secondary { background: #e2e8f0; color: #475569; border: none; }
.btn-soft-secondary:hover { background: #cbd5e1; }
.btn-soft-info { background: #e0f2fe; color: #0369a1; border: none; }
.btn-soft-info:hover { background: #bae6fd; }

.font-xs { font-size: 0.75rem; }
.font-sm { font-size: 0.85rem; }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }
.gap-4 { gap: 16px; }

.bg-soft-grey { background-color: #f1f5f9; }
.bg-soft-success { background-color: #ecfdf5 !important; }

/* ── FIXED TOP BAR ── */
.ev-fixed-top-bar { height: 64px; flex-shrink: 0; z-index: 10; }
.ev-stat-mini { padding: 0 16px; border-right: 1px solid #e2e8f0; }
.ev-stat-mini:last-child { border-right: none; }
.ev-stat-mini label { font-size: 0.6rem; font-weight: 800; color: #64748b; letter-spacing: 0.05em; margin: 0; text-transform: uppercase; }
.ev-stat-mini .value { font-size: 1.1rem; font-weight: 800; line-height: 1.2; }
.max-w-400 { max-width: 400px; }

/* ── SIDEBAR ── */
.ev-nav-item { cursor: pointer; transition: all 0.2s; border-left: 4px solid transparent !important; background: #fff; }
.ev-nav-item:hover { background: #f8fafc; border-left-color: #cbd5e1 !important; }
.ev-nav-item--active { background: #e0f2fe !important; border-left-color: #0369a1 !important; box-shadow: inset 0 0 10px rgba(3, 105, 161, 0.05); }
.ev-nav-item--done { background: #f0fdf4; }
.ev-nav-dot { width: 10px; height: 10px; border-radius: 50%; background: #cbd5e1; }
.ev-nav-item--active .ev-nav-dot { background: #0369a1; box-shadow: 0 0 0 4px rgba(3, 105, 161, 0.1); }
.ev-nav-item--done .ev-nav-dot { background: #22c55e; }

/* ── ITEM CARD ── */
.ev-item-card { transition: all 0.3s; border-radius: 16px !important; border: 2px solid #e2e8f0 !important; }
.ev-item-card--active { border-color: #3b82f6 !important; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1) !important; }
.ev-item-card--done { border-color: #22c55e !important; }
.bg-soft-blue { background-color: #f0f9ff !important; }

.ev-quant-destaque { font-size: 1.6rem; font-weight: 800; line-height: 1.1; }
.ev-quant-destaque small { font-size: 0.8rem; opacity: 0.7; }

/* ── LOTES ── */
.ev-lote-card { background: #f8fafc; border-color: #e2e8f0 !important; border-width: 2px !important; }
.ev-lote-card--active { background: #f1f5f9 !important; border-color: #3b82f6 !important; }
.ev-lote-card:hover { transform: translateY(-2px); box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); }

.ev-input-large { font-size: 1.5rem !important; height: 56px !important; border-radius: 12px !important; border-color: #cbd5e1; color: #0f172a !important; }
.ev-input-large:focus { border-color: #3b82f6; box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1); background: #fff; }

.shadow-inner { box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.05); }

/* ── BUTTONS ── */
.btn-soft-info { background: #e0f2fe; color: #0369a1; border: none; font-weight: 700; }
.btn-soft-info:hover { background: #bae6fd; }
.btn-soft-danger { background: #fee2e2; color: #b91c1c; border: none; }
.btn-soft-danger:hover { background: #fecaca; }

.transition-all { transition: all 0.2s; }
</style>



// WEBPACK FOOTER //
// src/views/expedicao/volumes/ExpedicaoVolumesModal.vue